import { FC, useMemo } from "react";

import { VenueBlockForm as TitleForm } from "Bands/components";
import {
  VenueSearch,
  CreateVenue,
  SelectedVenueTeaser,
} from "Bands/containers";
import {
  useVenueBlockContext,
  VenueBlockProvider,
  VenueBlockState,
} from "Bands/contexts";
import {
  useCreateVenueBlock,
  useEditVenueBlock,
  useVenueBlockForm,
} from "Bands/utils";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";
import { APIErrorMessage, BackButton, Button } from "shared/components";
import { IAPIState, ITheme } from "shared/types";

import { AddVenueCTA, LinkVenueButton } from "./components";

const { venueBlock: venueBlockCopy } = copy;

export interface VenueBlockFormProps {
  blockId?: string;
  title?: string | null;
  venueId?: string | null;
  stageId?: string | null;
  capacityId?: string | null;
  type?: "create" | "edit";
}

const VenueBlockFormComponent: FC<VenueBlockFormProps> = ({
  blockId,
  type = "create",
  ...rest
}) => {
  const { currentState, venueId, setBlockTitle } = useVenueBlockContext();

  const { apiMeta: createApiMeta, handleSubmit: handleCreate } =
    useCreateVenueBlock();

  const { apiMeta: editApiMeta, handleSubmit: handleEdit } =
    useEditVenueBlock(blockId);

  const {
    isSearched,
    goToSearch,
    handleCreateVenue,
    isSubmitDisabled,
    scrollRef,
    isVenueDirty,
    titleDefaultValues,
    goBackToSelectedVenue,
  } = useVenueBlockForm({ blockId, ...rest });

  const isLinkButtonDisabled = useMemo(
    () =>
      isSubmitDisabled ||
      !isVenueDirty ||
      createApiMeta?.status === IAPIState.pending ||
      editApiMeta?.status === IAPIState.pending,
    [createApiMeta?.status, editApiMeta?.status, isSubmitDisabled, isVenueDirty]
  );

  return (
    <Spacer size="l">
      <div ref={scrollRef}>
        <TitleForm
          defaultValues={titleDefaultValues}
          handleSubmit={() => {}}
          handleTitleChange={setBlockTitle}
        />
      </div>
      {currentState !== VenueBlockState.search && (
        <BackButton
          label={venueBlockCopy.buttons.backToSearch}
          onClick={goToSearch}
        />
      )}
      {!venueId && currentState === VenueBlockState.search && (
        <>
          <VenueSearch />
          {isSearched && <AddVenueCTA handleClick={handleCreateVenue} />}
        </>
      )}
      {!venueId && currentState === VenueBlockState.createVenue && (
        <CreateVenue />
      )}
      {venueId && (
        <>
          <SelectedVenueTeaser
            canCreateStageOrCapacity={
              currentState === VenueBlockState.createVenue
            }
          />
          <Button
            theme={ITheme.secondary}
            data-testid="add_another_venue_cta"
            onClick={goToSearch}
          >
            {venueBlockCopy.edit.chooseOtherVenue}
          </Button>
        </>
      )}
      {type === "edit" && !venueId && (
        <Button theme={ITheme.secondary} onClick={goBackToSelectedVenue}>
          {venueBlockCopy.edit.cancelChooseOtherVenue}
        </Button>
      )}
      <LinkVenueButton
        dataTestId="save_venue_block"
        label={type === "edit" ? copy.form.save : undefined}
        handleSubmit={type === "edit" ? handleEdit : handleCreate}
        isDisabled={isLinkButtonDisabled}
        isLoading={
          createApiMeta?.status === IAPIState.pending ||
          editApiMeta?.status === IAPIState.pending
        }
      />
      {(createApiMeta?.error || editApiMeta?.error) && (
        <APIErrorMessage
          apiErrorMessage={copy.errors.general}
          apiError={createApiMeta?.error || editApiMeta?.error}
        />
      )}
    </Spacer>
  );
};

export const VenueBlockForm: FC<VenueBlockFormProps> = ({
  title,
  venueId,
  stageId,
  capacityId,
  ...rest
}) => (
  <VenueBlockProvider
    defaultTitle={title}
    defaultVenueId={venueId}
    defaultStageId={stageId}
    defaultCapacityId={capacityId}
  >
    <VenueBlockFormComponent
      title={title}
      venueId={venueId}
      stageId={stageId}
      capacityId={capacityId}
      {...rest}
    />
  </VenueBlockProvider>
);
